import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  ClockIcon, 
  MapPinIcon, 
  TruckIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getApiBaseUrl } from '../../services/api';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const riderIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#009FE3" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#009FE3"/>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const pickupIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#F15A29" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const dropoffIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#10B981" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

interface TrackingData {
  order_uuid: string;
  order_status: string;
  status_description: string;
  rider_location?: { lat: number; lng: number };
  rider_location_age_seconds?: number;
  pickup_location: { lat: number; lng: number; address: string };
  dropoff_location: { lat: number; lng: number; address: string };
  rider_to_pickup_eta?: {
    distance_km: number;
    duration_minutes: number;
    duration_in_traffic_minutes: number;
    eta_timestamp: string;
  };
  rider_to_dropoff_eta?: {
    distance_km: number;
    duration_minutes: number;
    duration_in_traffic_minutes: number;
    eta_timestamp: string;
  };
  total_distance_km?: number;
  has_rider?: boolean;
  vehicle_type?: string;
  created_at: string;
  last_updated: string;
}

const MapController: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [map, center, zoom]);
  
  return null;
};

const PublicTracking: React.FC = () => {
  const { orderUuid } = useParams<{ orderUuid: string }>();
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);

  const fetchTrackingData = async (showLoading = true) => {
    if (!orderUuid) return;
    
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/public/track/${orderUuid}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Order not found. Please check your tracking link.');
        }
        throw new Error('Failed to load tracking information');
      }
      
      const data = await response.json();
      setTrackingData(data);
      setLastUpdateTime(new Date());
    } catch (err: any) {
      console.error('Error fetching tracking data:', err);
      setError(err.message || 'Failed to load tracking information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackingData();
    
    const interval = setInterval(() => {
      fetchTrackingData(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [orderUuid]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'pending_payment': 'bg-orange-100 text-orange-800 border-orange-300',
      'awaiting_payment': 'bg-orange-100 text-orange-800 border-orange-300',
      'assigned': 'bg-blue-100 text-blue-800 border-blue-300',
      'accepted': 'bg-blue-100 text-blue-800 border-blue-300',
      'picked_up': 'bg-purple-100 text-purple-800 border-purple-300',
      'in_transit': 'bg-purple-100 text-purple-800 border-purple-300',
      'in_progress': 'bg-purple-100 text-purple-800 border-purple-300',
      'delivered': 'bg-green-100 text-green-800 border-green-300',
      'cancelled': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status: string) => {
    if (['delivered'].includes(status)) {
      return <CheckCircleIcon className="w-6 h-6 text-green-600" />;
    }
    if (['in_transit', 'in_progress', 'picked_up'].includes(status)) {
      return <TruckIcon className="w-6 h-6 text-purple-600" />;
    }
    if (['cancelled'].includes(status)) {
      return <ExclamationCircleIcon className="w-6 h-6 text-red-600" />;
    }
    return <ClockIcon className="w-6 h-6 text-blue-600" />;
  };

  const getRoutePoints = (): [number, number][] => {
    if (!trackingData) return [];
    
    const points: [number, number][] = [];
    
    if (trackingData.rider_location && ['assigned', 'accepted'].includes(trackingData.order_status)) {
      points.push([trackingData.rider_location.lat, trackingData.rider_location.lng]);
      points.push([trackingData.pickup_location.lat, trackingData.pickup_location.lng]);
    } else if (trackingData.rider_location && ['in_progress', 'in_transit', 'picked_up'].includes(trackingData.order_status)) {
      points.push([trackingData.rider_location.lat, trackingData.rider_location.lng]);
      points.push([trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]);
    } else {
      points.push([trackingData.pickup_location.lat, trackingData.pickup_location.lng]);
      points.push([trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]);
    }
    
    return points;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !trackingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#009FE3] mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading delivery tracking...</p>
        </div>
      </div>
    );
  }

  if (error && !trackingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Tracking Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => fetchTrackingData()}
            className="px-6 py-3 bg-[#009FE3] text-white rounded-xl hover:bg-[#0089c9] transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!trackingData) return null;

  const currentETA = ['assigned', 'accepted'].includes(trackingData.order_status)
    ? trackingData.rider_to_pickup_eta
    : trackingData.rider_to_dropoff_eta;

  const mapCenter: [number, number] = trackingData.rider_location
    ? [trackingData.rider_location.lat, trackingData.rider_location.lng]
    : [(trackingData.pickup_location.lat + trackingData.dropoff_location.lat) / 2,
       (trackingData.pickup_location.lng + trackingData.dropoff_location.lng) / 2];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white">
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#009FE3] to-[#0089c9] rounded-xl flex items-center justify-center">
                <TruckIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Volexx Tracking</h1>
                <p className="text-sm text-gray-500">Live delivery updates</p>
              </div>
            </div>
            <button
              onClick={() => fetchTrackingData(false)}
              className="p-2 text-gray-500 hover:text-[#009FE3] hover:bg-sky-50 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {getStatusIcon(trackingData.order_status)}
                <div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(trackingData.order_status)}`}>
                    {trackingData.status_description}
                  </span>
                </div>
              </div>
              
              {currentETA && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Estimated arrival</p>
                  <p className="text-2xl font-bold text-[#009FE3]">
                    {currentETA.duration_in_traffic_minutes} min
                  </p>
                </div>
              )}
            </div>
          </div>

          {trackingData.has_rider && trackingData.order_status !== 'delivered' && trackingData.order_status !== 'cancelled' && (
            <div className="px-6 py-4 bg-gradient-to-r from-sky-50 to-white border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#009FE3] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  <TruckIcon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {['in_transit', 'in_progress', 'picked_up'].includes(trackingData.order_status) 
                      ? 'Rider En Route' 
                      : 'Rider Assigned'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {trackingData.vehicle_type ? `${trackingData.vehicle_type} Delivery` : 'En route to your location'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="h-72 sm:h-96">
            <MapContainer
              center={mapCenter}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController center={mapCenter} zoom={14} />
              
              <Marker position={[trackingData.pickup_location.lat, trackingData.pickup_location.lng]} icon={pickupIcon}>
                <Popup>
                  <div className="text-sm">
                    <strong className="text-[#F15A29]">Pickup</strong>
                    <p className="text-gray-600">{trackingData.pickup_location.address}</p>
                  </div>
                </Popup>
              </Marker>
              
              <Marker position={[trackingData.dropoff_location.lat, trackingData.dropoff_location.lng]} icon={dropoffIcon}>
                <Popup>
                  <div className="text-sm">
                    <strong className="text-green-600">Delivery</strong>
                    <p className="text-gray-600">{trackingData.dropoff_location.address}</p>
                  </div>
                </Popup>
              </Marker>
              
              {trackingData.rider_location && (
                <Marker position={[trackingData.rider_location.lat, trackingData.rider_location.lng]} icon={riderIcon}>
                  <Popup>
                    <div className="text-sm">
                      <strong className="text-[#009FE3]">Rider Location</strong>
                      {trackingData.vehicle_type && (
                        <p className="text-gray-600">{trackingData.vehicle_type} Rider</p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )}
              
              <Polyline
                positions={getRoutePoints()}
                color="#009FE3"
                weight={4}
                opacity={0.7}
                dashArray="10, 10"
              />
            </MapContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Delivery Details</h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPinIcon className="w-4 h-4 text-[#F15A29]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Pickup Location</p>
                <p className="text-gray-900">{trackingData.pickup_location.address}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPinIcon className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Delivery Location</p>
                <p className="text-gray-900">{trackingData.dropoff_location.address}</p>
              </div>
            </div>
            
            {trackingData.vehicle_type && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TruckIcon className="w-4 h-4 text-[#009FE3]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Delivery Method</p>
                  <p className="text-gray-900">{trackingData.vehicle_type}</p>
                </div>
              </div>
            )}
            
            {trackingData.total_distance_km && (
              <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                <span className="text-gray-500">Total Distance</span>
                <span className="font-medium text-gray-900">{trackingData.total_distance_km.toFixed(1)} km</span>
              </div>
            )}
          </div>
        </div>

        {lastUpdateTime && (
          <p className="text-center text-sm text-gray-400">
            Last updated: {lastUpdateTime.toLocaleTimeString()}
          </p>
        )}

        <div className="text-center pb-6">
          <p className="text-sm text-gray-500 mb-2">Powered by</p>
          <Link to="/" className="text-[#009FE3] font-bold text-lg hover:underline">
            Volexx Logistics
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicTracking;
